import React from 'react';

interface WorldStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWorld: (world: 'good' | 'bad') => void;
  clickedChoiceIndex?: number | null;
}

export const WorldStateModal: React.FC<WorldStateModalProps> = ({
  isOpen,
  onClose,
  onSelectWorld,
  clickedChoiceIndex,
}) => {
  const handleWorldSelection = (world: 'good' | 'bad') => {
    onSelectWorld(world);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-gradient-to-b from-[#050614] to-[#0f1a2f] border border-[#1a2d4d] rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-b border-[#1a2d4d] px-8 py-6">
          <h2 className="text-3xl font-bold uppercase bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] bg-clip-text text-transparent">
            Entering Segment 3: Parallel Worlds
          </h2>
          <p className="text-sm text-gray-400 mt-2">
            Choose which narrative path to develop first based on player choices
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-5xl mx-auto space-y-6">
            {clickedChoiceIndex !== null && clickedChoiceIndex !== undefined && (
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-4 shadow-lg">
                <p className="text-sm text-gray-300">
                  You clicked on{' '}
                  <span className="font-semibold text-[#fbbf24]">
                    Choice {clickedChoiceIndex + 1}
                  </span>
                  , which leads to the{' '}
                  <span
                    className={`font-semibold ${
                      clickedChoiceIndex === 0 || clickedChoiceIndex === 3
                        ? 'text-[#10b981]'
                        : 'text-[#ef4444]'
                    }`}
                  >
                    {clickedChoiceIndex === 0 || clickedChoiceIndex === 3 ? 'Better' : 'Worse'}{' '}
                    World
                  </span>{' '}
                  path.
                </p>
              </div>
            )}

            <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg space-y-4">
              <p className="text-gray-300">
                When we share personal stories or memories, it&apos;s never neutral--we&apos;re
                vulnerable. Whether seeking connection (risking the other person won&apos;t
                &quot;get&quot; the meaning) or intimidation (risking non-compliance), the
                listener&apos;s response is a high-stakes moment: it shows if the conversational
                strategy &apos;worked&apos;.
              </p>

              <p className="text-gray-300">
                So -- here, as a writer, put yourself in the shoes of the NPC. Imagine, as you work
                on these paths, what the NPC taking the players&apos; answers as &apos;better&apos;
                or &apos;worse&apos; might mean and manifest as. Consider that everything has been
                leading to this → has the NPC &apos;won&apos; the A-game by telling the story? The
                player, unwittingly, has made it so or not so by choosing the dialogue options that
                led here.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-[#031209] to-[#0a1a0f] border border-[#059669] rounded-xl p-6 shadow-lg">
                <h3 className="text-[#10b981] font-semibold mb-3 text-lg">
                  🌟 Better World (Segment 3A)
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {`Choices 1 & 4 lead here. Player responses have leaned towards making the NPC `}
                  <em>feel</em>
                  {` like their story 'worked' -- be it connection, compliance, a strategic edge gained, etc. This won't be stated literally -- but consider how someone who feels `}
                  <em>winning</em>
                  {` might behave differently.`}
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#2a0e0e] to-[#1a0a0a] border border-[#dc2626] rounded-xl p-6 shadow-lg">
                <h3 className="text-[#ef4444] font-semibold mb-3 text-lg">
                  💀 Worse World (Segment 3B)
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {`Choices 2 & 3 lead here. The NPC feels, based on the player's choice, that their story somehow missed its mark -- leading to the behaviour of someone who may feel rebuffed, retreating, in conflict, etc. Remember the A-Plot -- how might they now act?`}
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-[#4c1d95] rounded-xl p-5 shadow-lg">
              <p className="text-[#a78bfa] text-sm leading-relaxed">
                <span className="font-semibold">Note:</span> You can edit both worlds at any time
                using the Segment 3A and 3B buttons in the navigation. This choice just determines
                which world you&apos;ll start working on first. Both paths have identical structure
                but different content reflecting the NPC&apos;s emotional state based on how the
                player responded.
              </p>
            </div>

            <p className="text-gray-300 text-center italic">
              Select which world state you want to write content for first:
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-t border-[#1a2d4d] px-8 py-6">
          <div className="flex gap-6 justify-center">
            <button
              onClick={() => handleWorldSelection('good')}
              className="px-8 py-4 bg-gradient-to-r from-[#059669] to-[#047857] hover:from-[#047857] hover:to-[#065f46] text-white rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 hover:shadow-lg hover:scale-105"
            >
              🌟 Start with Better World
            </button>

            <button
              onClick={() => handleWorldSelection('bad')}
              className="px-8 py-4 bg-gradient-to-r from-[#dc2626] to-[#b91c1c] hover:from-[#b91c1c] hover:to-[#991b1b] text-white rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 hover:shadow-lg hover:scale-105"
            >
              💀 Start with Worse World
            </button>
          </div>

          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-3 text-gray-400 hover:text-gray-200 text-sm transition-colors duration-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
